import { useEffect, useMemo, useState, type SyntheticEvent } from "react";
import { ArrowLeft, Download, ExternalLink, X } from "lucide-react";
import {
  buildMailAttachmentUrl,
  downloadMailAttachment,
} from "@/lib/api/imap";
import { fetchMailAttachmentBlobCached } from "@/lib/cache/fetchMailAttachmentBlobCached";
import { getAttachmentPreviewKind } from "@/lib/mail/attachmentPreview";
import {
  isMobilePdfInlineUnsupported,
  openPdfBlobOnMobile,
  openUrlInNewTab,
  shouldUseMobilePdfShell,
} from "@/lib/mail/mobilePdfPreview";
import { MobilePdfPreviewPanel } from "@/features/message/MobilePdfPreviewPanel";
import { MailOfficePreview } from "@/features/message/MailOfficePreview";
import { MobileButton } from "@/mobile/components/ui/MobileButton";
import { MobileHeader } from "@/mobile/components/ui/MobileHeader";
import { MessageBodyPreloader } from "./MessageBodyPreloader";
import type { MailAttachment } from "@/lib/types/mail";

/** Laisse avaler le clic fantôme iOS (sinon la croix tape le Filtrer derrière). */
const CLOSE_CLICK_THROUGH_MS = 360;

type Props = {
  accountId: number;
  folder: string;
  messageUid: number;
  attachment: MailAttachment;
  onClose: () => void;
  /** split = panneau droit de la vue fractionnée */
  variant?: "full" | "split";
};

function normalizePreviewBlob(blob: Blob, attachment: MailAttachment): Blob {
  const kind = getAttachmentPreviewKind(attachment);
  if (kind === "pdf") {
    if (blob.type.includes("pdf") || attachment.filename.toLowerCase().endsWith(".pdf")) {
      return blob;
    }
    return new Blob([blob], { type: "application/pdf" });
  }
  if (kind === "image") {
    if (blob.type.startsWith("image/")) return blob;
    const ext = attachment.filename.split(".").pop()?.toLowerCase() ?? "png";
    const mime =
      ext === "jpg" || ext === "jpeg"
        ? "image/jpeg"
        : ext === "svg"
          ? "image/svg+xml"
          : ext === "gif"
            ? "image/gif"
            : ext === "webp"
              ? "image/webp"
              : `image/${ext}`;
    return new Blob([blob], { type: mime });
  }
  return blob;
}

export function AttachmentPreview({
  accountId,
  folder,
  messageUid,
  attachment,
  onClose,
  variant = "full",
}: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const kind = getAttachmentPreviewKind(attachment);
  const isPdf = kind === "pdf";
  const isImage = kind === "image";
  const isText = kind === "text";
  const isOffice = kind === "office";
  const isSplit = variant === "split";
  const useMobilePdfShell = isPdf && shouldUseMobilePdfShell();
  const mobilePdfNeedsPanel = useMobilePdfShell && isMobilePdfInlineUnsupported();
  const serverPdfUrl = useMemo(
    () =>
      buildMailAttachmentUrl({
        accountId,
        folder,
        uid: messageUid,
        part: attachment.part,
        filename: attachment.filename,
        inline: true,
      }),
    [accountId, attachment.filename, attachment.part, folder, messageUid]
  );

  const attachmentPart = attachment.part;
  const attachmentName = attachment.filename;

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setTextContent(null);
    setPreviewBlob(null);
    setObjectUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });

    if (isOffice) {
      setLoading(false);
      return;
    }

    void (async () => {
      try {
        const blob = await fetchMailAttachmentBlobCached({
          accountId,
          folder,
          uid: messageUid,
          part: attachmentPart,
          filename: attachmentName,
        });
        if (cancelled) return;
        if (isText) {
          const raw = await blob.text();
          setTextContent(raw);
          setLoading(false);
          return;
        }
        const normalized = normalizePreviewBlob(blob, attachment);
        setPreviewBlob(normalized);
        const url = URL.createObjectURL(normalized);
        setObjectUrl(url);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Impossible de charger la pièce jointe.");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountId, attachment, attachmentName, attachmentPart, folder, isOffice, isText, messageUid]);

  useEffect(() => {
    return () => {
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadMailAttachment({
        accountId,
        folder,
        uid: messageUid,
        part: attachment.part,
        filename: attachment.filename,
      });
    } finally {
      setDownloading(false);
    }
  }

  function handleOpenTab() {
    if (objectUrl && previewBlob && isPdf && useMobilePdfShell) {
      openPdfBlobOnMobile({
        blob: previewBlob,
        blobUrl: objectUrl,
        filename: attachment.filename,
      });
      return;
    }
    openUrlInNewTab(serverPdfUrl);
  }

  function handleClose(event?: SyntheticEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    window.setTimeout(() => onClose(), CLOSE_CLICK_THROUGH_MS);
  }

  const loadingLabel = isPdf
    ? "Chargement du PDF…"
    : isImage
      ? "Chargement de l’image…"
      : "Chargement du fichier…";

  const downloadBtn = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void handleDownload();
      }}
      disabled={downloading}
      className="rounded-lg p-1.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface)] hover:text-[var(--mail-text)] disabled:opacity-50"
      title="Télécharger"
      aria-label="Télécharger"
    >
      <Download className="h-3.5 w-3.5" />
    </button>
  );

  const openBtn = !isText && !isOffice ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleOpenTab();
      }}
      className="rounded-lg p-1.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface)] hover:text-[var(--mail-text)]"
      title="Ouvrir dans un nouvel onglet"
      aria-label="Ouvrir dans un nouvel onglet"
    >
      <ExternalLink className="h-3.5 w-3.5" />
    </button>
  ) : null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-[var(--mail-surface)]">
      {isSplit ? (
        <div className="flex shrink-0 items-center gap-1.5 border-b border-[var(--mail-border-soft)] bg-[var(--mail-bg)]/50 px-2.5 py-1.5">
          <p className="min-w-0 flex-1 truncate text-xs font-medium text-[var(--mail-text)]">
            {attachment.filename}
          </p>
          {downloadBtn}
          {openBtn}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface)] hover:text-[var(--mail-text)]"
            title="Fermer l’aperçu"
            aria-label="Fermer l’aperçu"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <MobileHeader
          title={attachment.filename}
          left={
            <MobileButton variant="icon" onClick={handleClose} aria-label="Fermer l’aperçu">
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </MobileButton>
          }
          right={
            <>
              <MobileButton
                variant="icon"
                onClick={() => void handleDownload()}
                disabled={downloading}
                aria-label="Télécharger"
              >
                <Download className="h-5 w-5" aria-hidden />
              </MobileButton>
              {!isText && !isOffice ? (
                <MobileButton variant="icon" onClick={handleOpenTab} aria-label="Ouvrir">
                  <ExternalLink className="h-5 w-5" aria-hidden />
                </MobileButton>
              ) : null}
            </>
          }
        />
      )}

      <div className="relative min-h-0 flex-1 overflow-hidden">
        {loading && !isOffice ? <MessageBodyPreloader overlay label={loadingLabel} /> : null}
        {error ? (
          <div className="flex h-full min-h-[8rem] items-center justify-center p-4">
            <p className="mail-alert-error text-sm" role="alert">
              {error}
            </p>
          </div>
        ) : null}
        {isOffice && !error ? (
          <MailOfficePreview
            accountId={accountId}
            folder={folder}
            messageUid={messageUid}
            attachment={attachment}
            editorType={isSplit ? "embedded" : "mobile"}
          />
        ) : null}
        {objectUrl && previewBlob && !error && isPdf && mobilePdfNeedsPanel ? (
          <MobilePdfPreviewPanel
            filename={attachment.filename}
            blob={previewBlob}
            blobUrl={objectUrl}
            serverUrl={serverPdfUrl}
          />
        ) : null}
        {objectUrl && !error && isPdf && !mobilePdfNeedsPanel ? (
          useMobilePdfShell ? (
            <embed
              src={objectUrl}
              type="application/pdf"
              title={attachment.filename}
              className="mail-attachment-preview-frame absolute inset-0 h-full w-full border-0 bg-neutral-100"
              onLoad={() => setLoading(false)}
            />
          ) : (
            <iframe
              src={objectUrl}
              title={attachment.filename}
              className="mail-attachment-preview-frame absolute inset-0 h-full w-full border-0 bg-neutral-100"
              onLoad={() => setLoading(false)}
            />
          )
        ) : null}
        {objectUrl && !error && isImage ? (
          <div className="absolute inset-0 overflow-auto bg-neutral-900/5 p-3">
            <img
              src={objectUrl}
              alt={attachment.filename}
              className="mx-auto max-h-full max-w-full object-contain"
              onLoad={() => setLoading(false)}
              onError={() => setError("Impossible d’afficher cette image.")}
            />
          </div>
        ) : null}
        {textContent != null && !error && isText ? (
          <pre className="absolute inset-0 overflow-auto whitespace-pre-wrap break-words bg-[var(--mail-bg)]/40 p-3 font-mono text-[11px] leading-relaxed text-[var(--mail-text)]">
            {textContent}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
